import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, Clock3, Building2, Plug, Sparkles, ShoppingCart } from "lucide-react";
import { motion } from "framer-motion";

const MODULES = [
    {
        id: "cf7-gmaps-autocomplete",
        name: "CF7 Autocomplete Entreprise",
        tagline: "L'auto-complétion Google Maps directement dans Contact Form 7.",
        desc: "Un champ « entreprise » qui interroge l'API Google Maps en temps réel. Vos prospects tapent 3 lettres, le nom, l'adresse et le SIRET tombent tout seuls. Fin des fautes de frappe, qualité de lead boostée.",
        Icon: Building2,
        accent: "217 91% 60%",
        accent2: "258 89% 66%",
        price: "29 € HT",
        url: "#",
        tags: ["WordPress", "Contact Form 7", "Google Maps API"],
    },
    {
        id: "horaires-google-temps-reel",
        name: "Horaires Google Live",
        tagline: "Affichez en temps réel l'état OUVERT / FERMÉ de la fiche Google.",
        desc: "Synchronisé en direct sur la plage horaire de la fiche Google Business Profile. Personnalisez le visuel OUVERT / FERMÉ aux couleurs du site et exposez les horaires du jour automatiquement. Plug & play.",
        Icon: Clock3,
        accent: "152 76% 52%",
        accent2: "199 89% 55%",
        price: "39 € HT",
        url: "#",
        tags: ["WordPress", "Shortcode", "Google Business"],
    },
];

export default function WPModulesSection() {
    return (
        <section id="modules" className="relative py-24 sm:py-32">
            {/* subtle decorative bg */}
            <div className="pointer-events-none absolute inset-0 -z-10">
                <div className="absolute left-1/2 top-0 h-px w-2/3 -translate-x-1/2 bg-gradient-to-r from-transparent via-border to-transparent" />
                <div className="absolute -left-20 top-1/3 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />
                <div className="absolute -right-20 bottom-1/3 h-72 w-72 rounded-full bg-accent/5 blur-3xl" />
            </div>

            <div className="mx-auto max-w-7xl px-4 sm:px-6">
                <div className="mx-auto max-w-3xl text-center">
                    <Badge
                        variant="outline"
                        className="rounded-full border-border bg-card/60 px-3 py-1 text-xs text-muted-foreground"
                        data-testid="wp-modules-badge"
                    >
                        <Plug className="mr-1.5 h-3 w-3" /> Add-ons WordPress
                    </Badge>
                    <h2
                        className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl"
                        data-testid="wp-modules-title"
                    >
                        Mini modules WP. <span className="text-gradient">Une fonctionnalité, un prix juste.</span>
                    </h2>
                    <p className="mt-5 text-base text-muted-foreground sm:text-lg" data-testid="wp-modules-subtitle">
                        Des plugins WordPress ultra-ciblés, sans surcouche, sans abonnement caché. Installez, activez, oubliez —
                        ça fonctionne tout seul.
                    </p>
                </div>

                <div className="mt-14 grid gap-6 md:grid-cols-2" data-testid="wp-modules-grid">
                    {MODULES.map((m) => (
                        <ModuleCard key={m.id} module={m} />
                    ))}
                </div>

                {/* Teaser more to come */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="mt-10 flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border bg-card/30 px-6 py-6 text-center sm:flex-row sm:gap-5 sm:text-left"
                    data-testid="wp-modules-coming-soon"
                >
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-primary-soft ring-1 ring-border">
                        <Sparkles className="h-5 w-5 text-accent animate-pulse-glow" />
                    </div>
                    <p className="text-sm text-muted-foreground sm:text-base">
                        <span className="font-medium text-foreground">D'autres modules arrivent —</span>{" "}
                        pensés sur le terrain, conçus pour ne faire qu'une chose, bien.
                    </p>
                </motion.div>
            </div>
        </section>
    );
}

const ModuleCard = ({ module: m }) => {
    const { id, name, tagline, desc, Icon, accent, accent2, price, url, tags } = m;

    return (
        <motion.article
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="group relative overflow-hidden rounded-2xl border border-border bg-gradient-card p-1"
            data-testid={`wp-module-card-${id}`}
        >
            {/* top accent line */}
            <div
                className="absolute inset-x-0 top-0 h-px opacity-70"
                style={{ background: `linear-gradient(90deg, transparent, hsl(${accent}), hsl(${accent2}), transparent)` }}
            />
            {/* glow corner */}
            <div
                className="absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-20 blur-3xl transition-opacity duration-700 group-hover:opacity-30"
                style={{ background: `radial-gradient(circle, hsl(${accent}), transparent 70%)` }}
            />

            <div className="relative flex h-full flex-col rounded-[calc(1rem-4px)] bg-background/40 p-6 sm:p-8">
                <div className="flex items-start justify-between gap-4">
                    <div
                        className="flex h-12 w-12 items-center justify-center rounded-xl"
                        style={{
                            background: `linear-gradient(135deg, hsl(${accent} / 0.25), hsl(${accent2} / 0.15))`,
                            border: `1px solid hsl(${accent} / 0.3)`,
                        }}
                    >
                        <Icon className="h-5 w-5" style={{ color: `hsl(${accent})` }} />
                    </div>
                    <Badge
                        variant="outline"
                        className="rounded-full border-border bg-card/60 font-mono text-[10px] uppercase tracking-wider text-muted-foreground"
                    >
                        Module WP
                    </Badge>
                </div>

                <h3 className="mt-5 font-display text-xl font-semibold text-foreground sm:text-2xl" data-testid={`wp-module-name-${id}`}>
                    {name}
                </h3>
                <p className="mt-2 font-display text-base font-medium text-foreground/90">{tagline}</p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{desc}</p>

                <div className="mt-5 flex flex-wrap gap-1.5">
                    {tags.map((t) => (
                        <span
                            key={t}
                            className="rounded-md border border-border bg-card/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground"
                        >
                            {t}
                        </span>
                    ))}
                </div>

                <div className="mt-auto flex flex-wrap items-end justify-between gap-3 pt-6">
                    <div>
                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Achat unique</div>
                        <div className="font-display text-xl font-semibold text-foreground" data-testid={`wp-module-price-${id}`}>
                            {price}
                        </div>
                    </div>
                    <Button
                        size="sm"
                        asChild
                        className="rounded-lg text-primary-foreground shadow-glow"
                        style={{ background: `linear-gradient(135deg, hsl(${accent}), hsl(${accent2}))` }}
                        data-testid={`wp-module-cta-${id}`}
                    >
                        <a href={url} className="inline-flex items-center gap-1.5">
                            <ShoppingCart className="h-3.5 w-3.5" /> Acheter en ligne
                            <ArrowUpRight className="h-3.5 w-3.5" />
                        </a>
                    </Button>
                </div>
            </div>
        </motion.article>
    );
};
